/**
 * Entity Investigation — the normalized, cross-module contract for a "story-first" investigation workspace.
 *
 * Any investigable entity (a finance transaction today; loans, payroll runs, assets, receipts, reconciliation
 * sessions later) produces this SAME shape. The frontend renders it with one generic shell + the shared
 * Timeline primitive, so new domains plug in by contributing timeline events, related objects, evidence, and
 * business context — WITHOUT redesigning the page. Read-only: this describes what happened, never how to change it.
 *
 * Enum-valued wire fields (`tone`, `type`, `entity`, `kind`) are typed as `string`/enums here (DTO shapes);
 * each producing module maps its own domain values in, and the frontend maps them to icons/colors/hrefs.
 */

import type { MoneyString } from "../finance/money";

/** Visual tone for a timeline node / badge — mirrors the shared Timeline's `TimelineTone`. */
export enum InvestigationTone {
  Default = "DEFAULT",
  Success = "SUCCESS",
  Warning = "WARNING",
  Destructive = "DESTRUCTIVE",
  Info = "INFO",
  Muted = "MUTED",
}

/**
 * Timeline event types contributed by a TRANSFER, as opposed to the audit-log actions that cover the
 * transaction itself.
 *
 * Shared because the backend emits these and the frontend maps each to an icon: matched as loose strings, a
 * rename on one side silently degrades every affected event to the default dot rather than failing a build.
 */
export enum TransferTimelineEvent {
  Requested = "TRANSFER_REQUESTED",
  Submitted = "TRANSFER_SUBMITTED",
  Approved = "TRANSFER_APPROVED",
  Rejected = "TRANSFER_REJECTED",
  Cancelled = "TRANSFER_CANCELLED",
}

/**
 * Timeline events contributed by the OTHER half of a reversal pair.
 *
 * A correction is two transactions — the original and the reversal that undoes it — and each one's own audit
 * rows stop at its own edge. Without these, a reversal's timeline began mid-story ("posted", with nothing said
 * about what it undid) and the original's ended before its ending. Each side borrows one event from the other so
 * the pair reads as a sequence.
 */
export enum ReversalTimelineEvent {
  /** On a reversal: the original transaction being undone was recorded. */
  OriginalRecorded = "REVERSAL_ORIGINAL_RECORDED",
  /** On an original: the reversal that undid it was posted. */
  ReversalPosted = "REVERSAL_POSTED",
}

/**
 * A pointer to another investigable/openable object. `entity` is a module-defined kind (e.g. "account",
 * "fund", "activity", "receipt", "reconciliation", "fiscalPeriod", "transaction", "journal", "member") that
 * the frontend's `resolveHref` maps to a route. `clickable` is false for a plain label with no destination.
 */
export interface InvestigationRef {
  entity: string;
  id: string;
  label: string;
  /**
   * Optional full i18n key for seeded finance/domain records whose stored label is canonical English.
   * Custom user-authored labels omit this and render literally.
   */
  translationKey?: string | null;
  clickable: boolean;
  /**
   * Optional date-range payload. A `fiscalPeriod` ref carries its own bounds here so the link can open that
   * window's transactions (the Explorer, date-filtered) instead of the bare investigation landing page.
   */
  dateFrom?: string | null;
  dateTo?: string | null;
}

/** One event in the entity's life. `type` is a module-defined semantic kind used to pick icon + tone. */
export interface InvestigationTimelineEvent {
  id: string;
  type: string;
  title: string;
  description?: string | null;
  timestamp: string;
  tone: string;
  actor?: { userId?: string | null; name: string; avatarUrl?: string | null } | null;
  /** Extra detail rows revealed on expand (e.g. old/new values of a change). */
  details?: Array<{ label: string; value: string }>;
  /** The object this event is about (e.g. the receipt for a "receipt issued" event). */
  ref?: InvestigationRef | null;
}

/** One step of the origin story (e.g. `Income → Sunday Service → General Fund`). */
export interface InvestigationContextStep {
  label: string;
  value: string;
  ref?: InvestigationRef | null;
}

/** Supporting evidence (receipt, payment reference, attachment, invoice, …). */
export interface InvestigationEvidenceItem {
  kind: string;
  label: string;
  status?: string | null;
  present: boolean;
  ref?: InvestigationRef | null;
}

export interface InvestigationAccountingLine {
  direction: string;
  /** Canonical decimal string at the impact's `currency` scale (IBA-116) — never a float. */
  amount: MoneyString;
  accountingAccountId: string;
  code: string;
  name: string;
  /** Optional full i18n key for seeded chart-of-accounts names. */
  translationKey?: string | null;
}

/** Optional accounting effect — present only for entities that post to the double-entry journal. */
export interface InvestigationAccountingImpact {
  journalEntryId: string;
  status: string;
  entryDate: string;
  /** Decided on the server, by comparing the two totals EXACTLY (integer minor units, no epsilon). */
  balanced: boolean;
  totalDebit: MoneyString;
  totalCredit: MoneyString;
  currency: string | null;
  reversesEntryId?: string | null;
  reversedByEntryId?: string | null;
  lines: InvestigationAccountingLine[];
}

export interface InvestigationBadge {
  label: string;
  tone: string;
}

export interface InvestigationHeader {
  entity: string;
  entityId: string;
  title: string;
  subtitle?: string | null;
  badges: InvestigationBadge[];
  /** The entity's headline money, as a canonical decimal string at `currency`'s scale (IBA-116). */
  amount?: MoneyString | null;
  currency?: string | null;
  occurredAt?: string | null;
}

/** One field in the "what happened" summary; `ref` makes the value a clickable pivot. */
export interface InvestigationSummaryField {
  label: string;
  value?: string | null;
  ref?: InvestigationRef | null;
}

/** The full normalized investigation of one entity — rendered by the generic workspace shell. */
export interface EntityInvestigation {
  header: InvestigationHeader;
  summary: InvestigationSummaryField[];
  timeline: InvestigationTimelineEvent[];
  accountingImpact?: InvestigationAccountingImpact | null;
  businessContext?: InvestigationContextStep[];
  evidence?: InvestigationEvidenceItem[];
  related: InvestigationRef[];
}
